import { Trans } from 'react-i18next';
import { t } from 'i18next';

import Link from '@mui/material/Link';

import {
  ColumnProps,
  Page,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { FilterOptionModel, ReadingUnitOrderWithFailureReasonModel } from 'models';
import { RouteNames } from 'config/routeNames';
import { PhotoAccess } from 'config/photosConfig';
import { formatDate } from 'utils/dates';
import { formatAddress, getPageName } from 'utils/format';
import { openPhotoInNewTab } from 'utils/getPhotoLink';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { useFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { useWorkerListSearch } from 'features/workers';
import { useValueSetList } from 'features/valueSets';
import { useExportUnreadableOrders, useUnreadableOrderList } from 'features/unreadableOrders';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import LeoSelectField from 'components/input-fields/leoSelectField/LeoSelectField';
import LeoDateInterval from 'components/input-fields/leoDateInterval/LeoDateInterval';
import LeoSelectFieldMulti from 'components/input-fields/leoSelectField/LeoSelectFieldMulti';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';
import { plannedReadingDateValidationSchema } from 'pages/Leo/Monitoring/ReadingResults/ReadingResultStatisticsPage';

const UnreadableOrdersPage = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onLoadQuery,
    onFilter,
    onSort,
    params,
    apiParams,
    exportParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams();

  const {
    data: unreadableOrders,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useUnreadableOrderList(apiParams);
  const {
    isLoading: isExportLoading,
    isRefetching,
    refetch,
  } = useExportUnreadableOrders(exportParams);
  const { data: valueSet, isLoading: isValueSetLoading } = useValueSetList({
    code: ['LEOKOZPONT', 'NLO', 'KT'],
  });

  const { data: workersData } = useWorkerListSearch({ withDeleted: true });

  const { activePanel, closePanel, setPanel } = usePanelState();

  const { data: leoKozpontData, accessor: leoKozpontAccessor } = useFieldDataAndAccessor(
    getValueSetValues(valueSet, 'LEOKOZPONT')
  );

  const { data: publicUtilityTypeData, accessor: publicUtilityTypeAccessor } =
    useFieldDataAndAccessor(getValueSetValues(valueSet, 'KT'));

  const { data: readingFailureReasonData, accessor: nloAccessor } = useFieldDataAndAccessor(
    getValueSetValues(valueSet, 'NLO'),
    'key',
    'value',
    true,
    'key'
  );
  const { data: workerData, accessor: workerAccessor } = useFieldDataAndAccessor(
    workersData,
    'sapId',
    'name',
    true
  );

  const headerActions = [
    {
      name: 'export',
      label: 'COMMON.EXPORT',
      icon: <Export fill="currentColor" />,
      onClick: () => refetch(),
      isLoading: isExportLoading || isRefetching,
      disabledIf: !exportParams?.filter,
    },
  ];

  const filters: FilterOptionModel[] = [
    {
      name: 'plannedReadingDate',
      headerTitle: 'LEO_LEO_EGYSEGEK.PLANNED_READING_DATE',
      component: <LeoDateInterval name="plannedReadingDate" />,
      panelFieldWidth: 12,
      panelFieldOffset: 12,
      divider: true,
      abbrName: t('FILTERABBR.PLANNED_READING_DATE'),
    },
    {
      name: 'worker',
      headerTitle: 'LEO_LEO_EGYSEGEK.WORKER',
      component: (
        <LeoSelectFieldMulti
          name="worker"
          data={workerData}
          accessor={workerAccessor}
          label="LEO_LEO_EGYSEGEK.WORKER"
          freeSolo
          maxLength={5}
        />
      ),
      divider: true,
      panelFieldWidth: 12,
      panelFieldOffset: 12,
    },
    {
      name: 'publicUtilityTypes',
      headerTitle: 'COMMON.PUBLIC_UTILITY_TYPE',
      component: (
        <LeoSelectFieldMulti
          name="publicUtilityTypes"
          data={publicUtilityTypeData}
          accessor={publicUtilityTypeAccessor}
          label="COMMON.PUBLIC_UTILITY_TYPE"
        />
      ),
      divider: true,
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.PUBLIC_UTILITY_TYPE'),
    },
    {
      name: 'readingFailureReasonKeys',
      headerTitle: 'LEO_LEO_EGYSEG_RESZLETEK.FAILURE_REASON',
      component: (
        <LeoSelectFieldMulti
          name="readingFailureReasonKeys"
          data={readingFailureReasonData}
          accessor={nloAccessor}
          label="LEO_LEO_EGYSEG_RESZLETEK.FAILURE_REASON"
          readOnly
        />
      ),
      divider: true,
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.READING_FAILURE_REASON'),
    },
    {
      name: 'bundle.from',
      headerTitle: 'LEO_LEOLVASO_STATISZTIKA.BUNDLE',
      component: (
        <LeoTextField
          name="bundle.from"
          label={<Trans i18nKey="LEO_LEO_EGYSEGEK.FROM" />}
          maxLength={10}
        />
      ),
      panelFieldWidth: 6,
    },
    {
      name: 'bundle.to',
      component: (
        <LeoTextField
          name="bundle.to"
          label={<Trans i18nKey="LEO_LEO_EGYSEGEK.TO" />}
          maxLength={10}
        />
      ),
      panelFieldWidth: 6,
      divider: true,
    },
    {
      name: 'readingCenter',
      headerTitle: 'LEO_LEO_KOZPONT_ALAPERTEKEK.READING_CENTER',
      component: (
        <LeoSelectField
          name="readingCenter"
          data={leoKozpontData}
          label="LEO_LEO_KOZPONT_ALAPERTEKEK.READING_CENTER"
        />
      ),
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.READING_CENTER'),
    },
  ];

  const unreadableOrdersFilterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.LEO_NEM_LEOLVASHATO_RENDELESEK),
    validationSchema: plannedReadingDateValidationSchema,
    isFetching,
  });

  const panels = [unreadableOrdersFilterConfig];

  const viewPhotoHandler = (order: ReadingUnitOrderWithFailureReasonModel) => {
    const readingOrderId = order.meter?.readingOrderId;

    if (readingOrderId) {
      openPhotoInNewTab(PhotoAccess.READING_UNIT_DETAILS, readingOrderId);
    }
  };

  const renderPhotoLink = (order: ReadingUnitOrderWithFailureReasonModel) => {
    const count = order.notReadablePhotoCount;

    if (count === undefined || count === null) {
      return '';
    }

    const photoLabel = `${count} db`;

    if (count <= 0 || !order.meter?.readingOrderId) {
      return photoLabel;
    }

    return (
      <Link
        component="button"
        type="button"
        variant="cellName"
        underline="none"
        fontWeight="bold"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          viewPhotoHandler(order);
        }}
      >
        {photoLabel}
      </Link>
    );
  };

  const columns: ColumnProps<ReadingUnitOrderWithFailureReasonModel>[] = [
    {
      key: 'readingUnit.bundle',
      header: <Trans i18nKey="NOTIFY_LOGS.BUNDLE" />,
      accessor: ({ readingUnit }) => readingUnit?.bundle,
      field: 'readingUnit.bundle',
      sortable: true,
    },
    {
      key: 'readingUnit.plannedReadingDate',
      header: <Trans i18nKey="NOTIFY_LOGS.PLANNED_READING_DATE" />,
      accessor: ({ readingUnit }) => formatDate(readingUnit?.plannedReadingDate),
      field: 'readingUnit.plannedReadingDate',
      sortable: true,
    },
    {
      key: 'readingUnit.readingUnitCode',
      header: <Trans i18nKey="NOTIFY_LOGS.READING_UNIT_CODE" />,
      accessor: ({ readingUnit }) => readingUnit?.readingUnitCode,
      field: 'readingUnit.readingUnitCode',
      sortable: true,
    },
    {
      key: 'readingUnit.readingCenter',
      header: <Trans i18nKey="NOTIFY_LOGS.READING_CENTER" />,
      accessor: ({ readingUnit }) => readingUnit?.readingCenter,
      field: 'readingUnit.readingCenter',
      sortable: true,
    },
    {
      key: 'worker.sapId',
      header: <Trans i18nKey="NOTIFY_LOGS.WORKER_ID" />,
      accessor: ({ worker }) => worker?.sapId,
      field: 'worker.sapId',
      sortable: true,
    },
    {
      key: 'partner.sapId',
      header: <Trans i18nKey="NOTIFY_LOGS.PARTNER" />,
      accessor: ({ partner }) => partner?.sapId,
      field: 'partner.sapId',
      sortable: true,
    },
    {
      key: 'partner.name',
      header: <Trans i18nKey="NOTIFY_LOGS.NAME" />,
      accessor: ({ partner }) => partner?.name,
      field: 'partner.name',
      sortable: true,
    },
    {
      key: 'consumptionPlace.address.city',
      header: <Trans i18nKey="NOTIFY_LOGS.ADDRESS" />,
      accessor: ({ consumptionPlace }) => formatAddress(consumptionPlace?.address),
      field: 'consumptionPlace.address.city',
      sortable: true,
    },
    {
      key: 'device.publicUtilityType',
      header: <Trans i18nKey="LEO_NEM_LEOLVASHATO_RENDELESEK.PUBLIC_UTILITY_TYPE" />,
      accessor: ({ device }) => publicUtilityTypeAccessor[device.publicUtilityType],
      field: 'device.publicUtilityType',
      sortable: true,
    },
    {
      key: 'device.bindingId',
      header: <Trans i18nKey="NOTIFY_LOGS.BINDING_ID" />,
      accessor: ({ device }) => device?.bindingId,
      field: 'device.bindingId',
      sortable: true,
    },
    {
      key: 'device.serialNumber',
      header: <Trans i18nKey="NOTIFY_LOGS.SERIAL_NUMBER" />,
      accessor: ({ device }) => device?.serialNumber,
      field: 'device.serialNumber',
      sortable: true,
    },
    {
      key: 'meter.readingOrderId',
      header: <Trans i18nKey="NOTIFY_LOGS.ORDER_ID" />,
      accessor: ({ meter }) => meter?.readingOrderId,
      field: 'meter.readingOrderId',
      sortable: true,
    },
    {
      key: 'notReadablePhotoCount',
      header: <Trans i18nKey="LEO_NEM_LEOLVASHATO_RENDELESEK.PHOTOS" />,
      accessor: (order) => renderPhotoLink(order),
    },
    {
      key: 'readingFailureReason.key',
      header: <Trans i18nKey="LEO_LEO_EGYSEG_RESZLETEK.FAILURE_REASON" />,
      accessor: ({ readingFailureReason }) => readingFailureReason?.key,
      tooltip: ({ readingFailureReason }) =>
        readingFailureReason?.key && getValueSetValues(valueSet, 'NLO', readingFailureReason.key),
      field: 'readingFailureReason.key',
      sortable: true,
    },
    {
      key: 'readingFailureReasonComment',
      header: <Trans i18nKey="COMMON.COMMENT" />,
      accessor: 'readingFailureReasonComment',
      sortable: true,
    },
    {
      key: 'readingResultDate',
      header: <Trans i18nKey="NOTIFY_LOGS.READING_RESULT_DATE" />,
      accessor: ({ readingResultDate }) => formatDate(readingResultDate),
      field: 'readingResultDate',
      sortable: true,
    },
  ];

  return (
    <Page
      panels={panels}
      activePanel={activePanel}
      title={<Trans i18nKey="LEO_NEM_LEOLVASHATO_RENDELESEK.TITLE" />}
      inScroll
      closePanel={closePanel}
      setPanel={setPanel}
    >
      {!isValueSetLoading && (
        <Table
          filterDetailsData={{ leoKozpontAccessor, nloAccessor, publicUtilityTypeAccessor }}
          panels={panels}
          activePanel={activePanel}
          setPanel={setPanel}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          list={unreadableOrders?.data || []}
          total={unreadableOrders?.meta.total}
          timestamp={dataUpdatedAt}
          enableCheckbox={false}
          enableTableView={true}
          loading={isLoading}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={columns}
          pageHeaderActions={headerActions}
          onLoadQuery={onLoadQuery}
        />
      )}
    </Page>
  );
};

export default UnreadableOrdersPage;
